import { useState } from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { SendAsModel } from '../../../../../../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  betaGetGmailSendAs,
  getUserQuery,
} from '../../../../../api/users-api';
import DeleteSendAsDialog from './delete-send-as-dialog';
import AddSendAsDialog from './add-send-as-dialog';
import EditSendAsDialog from './edit-send-as-dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { useParams } from 'react-router-dom';
import { Messages } from '../../../../../utils/message-constants';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Mail from '@mui/icons-material/Mail';

export default function SendAsPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();
  const { data: user } = useQuery(getUserQuery(userEmail));

  const queryClient = useQueryClient();
  const [openAddAccount, setOpenAddAccount] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<SendAsModel>();
  const [openEditAccount, setOpenEditAccount] = useState(false);
  const [openDeleteAccount, setOpenDeleteAccount] = useState(false);

  const {
    data: sendAsAccounts,
    isLoading,
    isFetching: sendAsAccountsFetching,
  } = useQuery({
    queryKey: [UserKeys.SendAsAccounts, userEmail],
    queryFn: () => betaGetGmailSendAs(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('send as accounts'),
    },
  });

  const columns: GridColDef[] = [
    {
      field: 'sendAsEmail',
      headerName: 'Email',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 4,
    },
    {
      field: 'displayName',
      headerName: 'Name',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 2,
    },
    {
      field: 'replyToAddress',
      headerName: 'Reply Address',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 3,
    },
    {
      field: 'treatAsAlias',
      headerName: 'Is Alias',
      editable: false,
      sortable: false,
      filterable: false,
      display: 'flex',
      type: 'boolean',
      valueGetter: (params) => {
        return params;
      },
    },
    {
      field: 'isPrimary',
      headerName: 'Is Primary',
      editable: false,
      sortable: false,
      filterable: false,
      display: 'flex',
      description:
        'This address is the primary address used to login to the account. If no default address is currently selected then this address is your default.',
      type: 'boolean',
      valueGetter: (params) => {
        return params;
      },
    },
    {
      field: 'isDefault',
      headerName: 'Is Default',
      editable: false,
      sortable: false,
      filterable: false,
      display: 'flex',

      description:
        'This address is selected as the default "From:" address in situations such as composing a new message or sending a vacation auto-reply.',
      type: 'boolean',
      valueGetter: (params) => {
        return params;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: (params: GridRowParams) => {
        // TODO: THIS NEEDS A EDIT PROPERTY BASED ON USER ROLES, CURRENTLY NO EDIT SEND AS USER ROLE EXISTS
        const buttonArray = [
          <GridActionsCellItem
            key="edit"
            label="Edit"
            icon={
              <Tooltip arrow title="Edit">
                <Edit />
              </Tooltip>
            }
            onClick={() => {
              setSelectedAccount(params.row);
              setOpenEditAccount(true);
            }}
          />,
        ];

        // TODO: THIS NEEDS A DISABLED PROPERTY BASED ON USER ROLES, CURRENTLY NO DELETE SEND AS USER ROLE EXISTS
        buttonArray.push(
          <GridActionsCellItem
            label="Delete"
            key="delete"
            icon={
              <Tooltip arrow title="Delete">
                <Delete />
              </Tooltip>
            }
            disabled={params.row.isPrimary === true}
            onClick={() => {
              setSelectedAccount(params.row);
              setOpenDeleteAccount(true);
            }}
          />
        );
        return buttonArray;
      },
    },
  ];

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddAccount(true)}
            disabled={
              !isAbleToSeeUIElement(userRoles, UserRoles.AddGmailSendAs.value)
            }
            label="Add Send As"
            tooltipLabel={
              !isAbleToSeeUIElement(userRoles, UserRoles.AddGmailSendAs.value)
                ? Messages.Errors.Permission.Acting(
                    UserRoles.AddGmailSendAs.name
                  )
                : 'Add Send As'
            }
          />
        }
        secondaryButtons={
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.SendAsAccounts, userEmail],
              });
            }}
            tooltipLabel="Refresh"
          />
        }
      />
    );
  }

  const sortedAccounts = sendAsAccounts
    ? [
        ...sendAsAccounts.sort((a, b) =>
          a.sendAsEmail.localeCompare(b.sendAsEmail)
        ),
      ]
    : [];

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Send As Addresses', <Mail />),
        }}
        toolbarQuickFilterWidth={400}
        getRowId={(row) => row.sendAsEmail}
        rows={sortedAccounts}
        columns={columns}
        loading={isLoading || sendAsAccountsFetching}
        disableColumnSelector
        disableColumnMenu
        disableRowSelectionOnClick
      />
      {selectedAccount ? (
        <DeleteSendAsDialog
          open={openDeleteAccount}
          handleClose={() => setOpenDeleteAccount(false)}
          userEmail={userEmail}
          selectedAccount={selectedAccount}
          sendAsAccounts={sendAsAccounts ? sendAsAccounts : []}
        />
      ) : null}
      <AddSendAsDialog
        open={openAddAccount}
        handleClose={() => setOpenAddAccount(false)}
        userEmail={userEmail}
        sendAsAccounts={sendAsAccounts ? sendAsAccounts : []}
        changeLogRoles={changeLogRoles}
      />
      {selectedAccount ? (
        <EditSendAsDialog
          open={openEditAccount}
          handleClose={() => setOpenEditAccount(false)}
          userEmail={userEmail}
          sendAsAccounts={sendAsAccounts ? sendAsAccounts : []}
          selectedAccount={selectedAccount}
        />
      ) : null}
    </>
  );
}
